import { useMemo, useState, useEffect, useCallback } from 'react';
import { PagingQuery } from 'tea-component';
import useIntlExtend from '@/pages/intelligence/hooks/useIntlExtend';
import useStoreRankMoreCoreLineChart from '@/pages/intelligence/pages/StoreRanks/components/StoreRankMoreCore/hooks/useStoreRankMoreCoreLineChart';
import { changeMiniGameRankingsMoreFormat } from '@intelligence/utils/dateUtils';
import { MINI_GAME_RANKING_MORE_TABLE_TITLE_MAP } from '@intelligence/v3/pages/ReusePages/CasualMiniGameRankings/config';
import { fetchGameGenreProfileMetricsNoType } from '@intelligence/services/common';
import { buriedPointPage } from '@/pages/home/utils/buriedPoint';
import { TOPGAMENUM } from '@/pages/intelligence/pages/TopCharts/_config/topChartsConst';
import { handleTableDefaultSelectGames } from '@/pages/intelligence/utils/tableUtils';
import {
  analysisPlatformTopInfo,
  getStoreRankPlatformName,
  getStoreRankTopName,
  getLineFormatData,
  formatMiniGameParams,
} from '@/pages/intelligence/_config/storeRank/utils';
import moment from 'moment';
import type { RecordItemType } from '@/pages/intelligence/components/SearchRecent/type';
import type {
  TableSelectionType,
  TableSelectStatusType,
} from '@/pages/intelligence/components/TableComponents/components/TableCheckbox';
import type { SortByItemType } from '@/pages/intelligence/components/TableComponents/components/SortBy';
import type {
  IStoreRankMoreCoreRankPrimaryKey,
  IStoreRankMoreCoreFetchParams,
  IStoreRankMoreCoreRank,
  IStoreRankMoreCoreTrend,
} from '@/pages/intelligence/types/storeRank';

const getIsLineVisualsNoData = (
  data: { [key: string]: IStoreRankMoreCoreTrend[] },
  segment: string,
): boolean => {
  const segmentData = data?.[segment];
  if (Array.isArray(segmentData)) {
    const flag1 = segmentData?.length > 0;
    const flag2 = segmentData?.some((item) => item?.history?.length > 0);
    return !(flag1 && flag2);
  }
  return true;
};

export default function useMiniGameRankingsMore(props: {
  entityType: 'mobile' | 'pc' | 'console' | 'minigame';
  pageLogCfg?: any;
  initialTopChartsName?: any;
  isEN?: any;
  getChangeLocalData?: any;
  primaryKey: IStoreRankMoreCoreRankPrimaryKey;
  platformTopMetrics: string[];
  initialFetchParams: IStoreRankMoreCoreFetchParams;
}) {
  const {
    entityType,
    pageLogCfg,
    primaryKey,
    platformTopMetrics,
    initialFetchParams,
    initialTopChartsName,
    isEN,
    getChangeLocalData,
  } = props;



  const { locale, formatMessage } = useIntlExtend();

  const [fetchParams, setFetchParams] = useState<IStoreRankMoreCoreFetchParams>(initialFetchParams);

  const [isVisualisLoading, setIsVisualisLoading] = useState(true);

  const [isTableLoading, setIsTableLoading] = useState(true);
  const [tableDimensionsData, setTableDimensionsData] = useState<string[]>([]);
  const [tableData, setTableData] = useState<IStoreRankMoreCoreRank[]>([]);
  const [tableTotal, setTableTotal] = useState(0);
  const [lineDataList, setLineDataList] = useState<IStoreRankMoreCoreTrend[]>([]);

  // 前端分页和排序相关状态
  const [allTableData, setAllTableData] = useState<IStoreRankMoreCoreRank[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(100);
  const [sortConfig, setSortConfig] = useState<{ orderMetric: string; order: string }>({
    orderMetric: '',
    order: 'desc',
  });

  // 宽表勾选的数据
  // 是否重新勾选过游戏
  const [operatedGame, setOperatedGame] = useState(false);
  const [defaultSelectionGames, setDefaultSelectionGames] = useState<TableSelectionType[]>([]);
  const [tableSelection, setTableSelection] = useState<TableSelectionType[]>([]);
  const [tableDimensionsForClearSearch, setTableDimensionsForClearSearch] = useState<string[]>([]);

  const allTableDataCur = useMemo(() => {
    return allTableData?.map((item: any) => {
      return {
        ...item,
        app_name: (isEN && item?.app_name_en) || item?.app_name,
        app_name_cn: item?.app_name,
        name: (isEN && item?.app_name_en) || item?.app_name,
        publisher: (isEN && item?.publisher_en) || item?.publisher,
        publisher_cn: item?.publisher,
        minigame_genre: (isEN && item?.minigame_genre_en) || item?.minigame_genre,
        minigame_sub_genre: (isEN && item?.minigame_sub_genre_en) || item?.minigame_sub_genre,
        minigame_genre_cn: item?.minigame_genre,
        minigame_sub_genre_cn: item?.minigame_sub_genre,
        desc: (isEN && item?.app_desc_en) || item?.app_desc
      };
    });
  }, [isEN, allTableData]);

  const lineData = useMemo(() => {
    if (Array.isArray(lineDataList) && lineDataList?.length > 0) {
      const localList = getChangeLocalData?.(lineDataList, isEN);
      const startDate = moment(fetchParams?.end_date ?? '')
        .subtract(30, 'days')
        .format('YYYY-MM-DD');
      const endDate = moment(fetchParams?.end_date ?? '').format('YYYY-MM-DD');
      // 将lineDataRes以game_id相同的排
      const newList = getLineFormatData(localList, initialTopChartsName, tableSelection, startDate, endDate);
      return newList;
    }
    return {};
  }, [lineDataList, isEN, tableSelection, fetchParams]);

  // 折线图相关hooks
  const {
    seriesData,
    xAxisData,
    yAxisFormatter,
    // @ts-ignore
    tooltipFormatter,
    createLegendsCustomMade,
    currentLine,
    segments,
    setSegment,
    segment,
  } = useStoreRankMoreCoreLineChart({
    entityType,
    topChartName: fetchParams?.topchart_name,
    dateType: fetchParams?.granularity,
    platformTopMetrics,
    data: lineData,
  });

  const isLineVisualisNoData = useMemo(
    () => getIsLineVisualsNoData(lineData, segment),
    [lineData, segment],
  );

  const sortData = (data: IStoreRankMoreCoreRank[], orderMetric: string, order: string) => {
    if (!orderMetric || !data.length) return data;
    return [...data].sort((a, b) => {
      const aValue = a[orderMetric as keyof IStoreRankMoreCoreRank];
      const bValue = b[orderMetric as keyof IStoreRankMoreCoreRank];
      // 处理 null 或 undefined
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      // 数字类型比较
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        // 反转排序：升序时显示数值大的，降序时显示数值小的
        return order === 'asc' ? bValue - aValue : aValue - bValue;
      }
      // 字符串类型比较
      const aStr = String(aValue);
      const bStr = String(bValue);
      return order === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
    });
  };

  // 前端分页函数
  const paginateData = useCallback(
    (data: IStoreRankMoreCoreRank[], page: number, size: number) => {
      const startIndex = (page - 1) * size;
      const endIndex = startIndex + size;
      return data.slice(startIndex, endIndex);
    },
    [],
  );

  // 更新表格数据（应用排序和分页）
  const updateTableDisplay = useCallback((
    allTableDataTemp: IStoreRankMoreCoreRank[] = allTableData,
    page = currentPage,
    pageSize = currentPageSize,
    sortConfObj: any = sortConfig,
  ) => {
    if (!allTableDataTemp.length) {
      setTableData([]);
      setTableTotal(0);
      return;
    }

    // 1. 排序
    const sortedData = sortData(allTableDataTemp, sortConfObj.orderMetric, sortConfObj.order);
    // const sortedData = allTableDataTemp;

    // 2. 更新总数
    setTableTotal(sortedData.length);

    // 3. 分页
    const paginatedData = paginateData(sortedData, page, pageSize);
    setTableData(paginatedData);
  }, [allTableData, sortConfig, currentPage, currentPageSize, sortData, paginateData]);

  const { platform, top } = useMemo(
    () => analysisPlatformTopInfo(fetchParams?.topchart_name),
    [fetchParams?.topchart_name],
  );

  const { platformName, topName } = useMemo(() => {
    const platformName = getStoreRankPlatformName(entityType, platform);
    const topNameLocaleKey = getStoreRankTopName(entityType, top, formatMessage);
    const topName = formatMessage({ id: topNameLocaleKey });
    return { platformName, topName };
  }, [locale, platform, top]);

  const getGameData = async (
    params: IStoreRankMoreCoreFetchParams,
    isCheckOrSearch: boolean,
    topChartsNameValue: string = initialTopChartsName,
  ) => {

    try {
      setTableDimensionsData([]);
      setTableData([]);
      setTableTotal(0);

      const fetchParamsValue = {
        ...formatMiniGameParams(params),
        group_by: ['game_id', 'minigame_type'],
        minigame_types: [topChartsNameValue],
        page: 1,
        page_size: 100000, // 获取所有数据
      } as any;

      const orderMetric = params?.order_metric ?? 'game_rank';
      const order = params?.order ?? 'desc';

      setFetchParams({
        ...params,
        ...fetchParamsValue,
        order_metric: orderMetric,
        order,
      });
      delete fetchParamsValue.single_source_select;
      // 接口调用前，检查start_date和end_date是否合法时间参数再调用接口,否则都赋值为''
      const res = await fetchGameGenreProfileMetricsNoType(fetchParamsValue);
      const resData = res?.data ?? {};
      const metricList = MINI_GAME_RANKING_MORE_TABLE_TITLE_MAP[topChartsNameValue];
      const listTemp = resData?.list ?? [];
      const listFormat = changeMiniGameRankingsMoreFormat(listTemp);
      const resSortList = sortData(listFormat, orderMetric, order) || [];
      const total = resData?.total ?? resSortList?.length; // 搜索不返回total用表格长度计算

      // 存储所有数据用于前端分页和排序
      setAllTableData(resSortList);
      setTableDimensionsData(metricList);
      setTableTotal(total);

      // 重置分页和排序状态
      setCurrentPage(1);
      setCurrentPageSize(100);
      setSortConfig({ orderMetric, order });
      updateTableDisplay(resSortList, 1, 100, { orderMetric, order });
      if (fetchParamsValue?.page === 1 && !isCheckOrSearch) {
        /** 默认选择第一页的前6作为表格全选展示 */
        const defaultGames = handleTableDefaultSelectGames(resSortList?.slice(0, TOPGAMENUM), primaryKey);
        setDefaultSelectionGames(defaultGames);
        /** 在第一页且没有勾选游戏时默认选中前6（游戏在排序或重新筛选时有更新） */
        if (!isCheckOrSearch) {
          setTableSelection(defaultGames);
        }
      }
      if (!fetchParamsValue?.app_ids?.length) {
        setTableDimensionsForClearSearch(metricList);
      }

      return res;
    } catch (err) {
      return null;
    }
  };

  const getLineChartData = async (params: IStoreRankMoreCoreFetchParams, topChartsNameValue: string = initialTopChartsName) => {
    try {
      setLineDataList([]);
      const formatParams = formatMiniGameParams(params);
      const fetchParamsValue = {
        ...formatParams,
        start_date: moment(formatParams?.end_date ?? '')
          .subtract(30, 'days')
          .format('YYYY-MM-DD'),
        // end_date: moment(params?.end_date ?? '').format('YYYY-MM-DD'),
        order_by: ['game_rank asc'],
        group_by: ['game_id', 'date', 'minigame_type'],
        minigame_types: [topChartsNameValue],
        page: 1,
        page_size: 100000, // 获取所有数据
      } as any;
      const res = await fetchGameGenreProfileMetricsNoType(fetchParamsValue);
      // const res = await fetchStoreRankMoreTrends(params);
      const lineDataRes = res?.data?.list ?? {};
      setLineDataList(lineDataRes);
    } catch (e) {
      console.error(e);
    } finally {
    }
  };

  const handleClearSearch = () => {
    setTableDimensionsData(tableDimensionsForClearSearch);
    // 清空搜索，不需要调接口，返回当前排序、页码、页数的数据即可
    updateTableDisplay(allTableData, currentPage, currentPageSize, sortConfig);
  };

  const transTableDataForSearch = (res: any) => {
    const resultList = res?.data?.list;
    if (!Array.isArray(resultList) || !resultList?.length) {
      return [];
    }
    // 如果是移动端则使用unified_id，否则使用primaryKey
    const idKey = 'game_id';
    const tranksList: RecordItemType[] = resultList.map((item) => ({
      id: item?.[idKey],
      imgSrc: item?.app_cover,
      lineOneStr: item?.app_name,
      // lineTwoImg: '',
      lineTwoImgName: '',
      country_abbre: 'noCountry',
      lineTwoStr: item?.publisher,
      showImgTwo: false,
    }));
    return tranksList;
  };

  const handleConfirm = (payload: any, topChartsNameValue: string = initialTopChartsName) => {
    let fetchParamsTmp = {
      ...fetchParams,
      ...payload,
      order_metric: 'game_rank',
      order: 'desc',
      ratio_type: 0,
      page: 1,
      page_size: 100,
    };
    if (payload?.single_source_select) {
      fetchParamsTmp = {
        ...fetchParamsTmp,
        order_source: payload?.single_source_select
      }
    }
    // 请求接口
    setIsTableLoading(true);
    setIsVisualisLoading(true);
    setOperatedGame(false);
    getData(fetchParamsTmp, false, true, topChartsNameValue);
  };

  const handleReset = () => { };

  const handleInit = () => { };

  const getData = (
    params: IStoreRankMoreCoreFetchParams,
    isCheckGame: boolean,
    isInit = false,
    topChartsNameValue = initialTopChartsName,
  ) => {
    getGameData(
      {
        ...params,
        app_ids: [],
      },
      isCheckGame,
      topChartsNameValue,
    )
      .then((res: any) => {
        setIsTableLoading(false);
        if (res && res?.code === 0) {
          if (isInit || !isCheckGame) {
            const listTemp = res?.data?.list ?? [];
            const orderMetric = params?.order_metric ?? '';
            const order = params?.order ?? 'desc';
            const resSortList = sortData(listTemp, orderMetric, order) || [];
            /** 默认选择第一页的前6作为表格全选展示 */
            const defaultGames = handleTableDefaultSelectGames(resSortList?.slice(0, TOPGAMENUM), primaryKey);
            const idList = defaultGames?.map((item: any) => {
              return item.game_id;
            });
            if (idList.length > 0) {
              const newParams = {
                ...params,
                app_ids: idList,
              } as any;
              getChartData(newParams, topChartsNameValue);
            } else {
              setLineDataList([]);
              setIsVisualisLoading(false);
            }
          }
        }
      })
      .catch(() => {
        setIsTableLoading(false);
      });
  };

  const getChartData = (params: IStoreRankMoreCoreFetchParams, topChartsNameValue: string = initialTopChartsName) => {
    const isSectionalTmp = (params?.regions?.length ?? 0) > 1;
    if (isSectionalTmp) return;
    setIsVisualisLoading(true);
    getLineChartData({
      ...params,
      page: 1,
      page_size: 100000,
    }, topChartsNameValue)
      .then(() => {
        setIsVisualisLoading(false);
      })
      .catch(() => {
        setIsVisualisLoading(false);
      });
  };

  const getTableResultList = (keyword: string) => {
    if (!keyword?.trim()) {
      // 如果没有关键词，重置为所有数据
      updateTableDisplay(allTableData, 1, currentPageSize, sortConfig);
      setTableSelection(defaultSelectionGames);
      return;
    }

    // 使用前端搜索过滤数据
    const keywordLower = keyword.toLowerCase().trim();
    const filteredData = allTableDataCur.filter((item: any) => {
      const appName = (item?.app_name || item?.name || '')?.toLowerCase();
      const publisher = (item?.publisher || '')?.toLowerCase();
      const gameId = (item?.game_id || item?.app_id || '')?.toString().toLowerCase();

      return (
        appName.includes(keywordLower) ||
        publisher.includes(keywordLower) ||
        gameId.includes(keywordLower)
      );
    });

    // 更新表格显示为搜索结果
    updateTableDisplay(filteredData, 1, currentPageSize, sortConfig);
  };

  const onTableSortChage = (e: SortByItemType) => {
    const orderMetric = e?.sortMetric ?? '';
    const order = e?.sortVal ?? 'desc';

    // 更新排序配置，前端处理排序
    setSortConfig({ orderMetric, order });
    setCurrentPage(1); // 排序时重置到第一页

    // 更新 fetchParams（用于其他逻辑）
    const fetchParamsTmp: any = {
      ...fetchParams,
      order_by: [`${orderMetric} ${order}`],
      page: 1,
      order_source: e?.orderSource ?? '',
      order_metric: orderMetric,
      order,
      ratio_type: e?.ratioType ?? 0,
    };
    setFetchParams(fetchParamsTmp);
    updateTableDisplay(allTableData, 1, currentPageSize, { orderMetric, order });
  };

  const onTablePagingChange = (e: PagingQuery) => {
    const pageIndex = e?.pageIndex ?? 1;
    const pageSize = e?.pageSize ?? 100;
    // 前端分页处理
    setCurrentPage(e?.pageIndex ?? 1);
    setCurrentPageSize(e?.pageSize ?? 100);
    updateTableDisplay(allTableData, pageIndex, pageSize, sortConfig);
  };

  // 表格列表项选择事件回调
  const onTableCheckGame = (
    selectList: TableSelectionType[],
    checkStatus: TableSelectStatusType,
  ) => {
    // setIsNewCheckGame(true);
    const idList = selectList?.map((item) => item?.[primaryKey] || '') ?? [];

    if (idList.length > 0) {
      const fetchParamsTmp = {
        ...fetchParams,
        page: 1,
        page_size: 10000,
        app_ids: [...idList],
      };
      getChartData(fetchParamsTmp);
    } else {
      setLineDataList([]);
    }

    // /**是否勾选了游戏 */
    if (checkStatus === 'cancel' || checkStatus === 'all' || selectList.length === 0) {
      setOperatedGame(false);
    } else {
      setOperatedGame(true);
    }
    setTableSelection(selectList);
  };

  const handlePageInit = (payload: Partial<IStoreRankMoreCoreFetchParams>) => {
    const fetchParamsTmp = {
      ...fetchParams,
      ...payload,
    };
    setIsTableLoading(true);
    setIsVisualisLoading(true);
    getData(fetchParamsTmp, operatedGame, true);
  };

  useEffect(() => {
    if (pageLogCfg) {
      /** 页面打点 */
      buriedPointPage(pageLogCfg);
    }
  }, []);

  return {
    fetchParams,
    platform,
    platformName,
    top,
    topName,
    handleInit,
    handleConfirm,
    handleReset,
    isVisualisLoading,
    isVisualisNoData: !isVisualisLoading && isLineVisualisNoData,
    seriesData,
    xAxisData,
    currentLine,
    segments,
    segment,
    setSegment,
    yAxisFormatter,
    tooltipFormatter,
    createLegendsCustomMade,
    isTableLoading,
    isTableNoData: !isTableLoading && !tableData?.length,
    tableDimensionsData,
    tableTotal,
    tableData,
    tableSelection,
    defaultSelectionGames,
    transTableDataForSearch,
    getTableResultList,
    handleClearSearch,
    onTableSortChage,
    onTablePagingChange,
    onTableCheckGame,
    onPageInit: handlePageInit,
    allTableData: allTableDataCur,
    currentPage,
    currentPageSize,
  };
}
